import {useEffect, useReducer} from "react";
import {useMatch, useNavigate, useResolvedPath} from "react-router-dom";

/**
 * This hook keep the state of a modal dialog (open, closed, loading) and
 * provides the functions to update the state when opening and closing the dialog.
 *
 * If a relative path is given the hook modify the browser path when calling the
 * open/close functions by adding/removing the given relative path.
 *
 * The path is relative to the parent route where the hook is called.
 * For example if the component calling the hook is located at /resource
 * and the given path is "edit" then the open function navigate to /resource/new,
 * while the close function go back to /resource.
 *
 * Note that the hook only control the state of the dialog and does not actually
 * open/close the dialog itself. The component using the hook is responsible
 * to show/hide the dialog when the hook state is open/close or when the browser
 * path match the given path.
 *
 * @param path an optional path (string)
 * @param onClosePath an optional path (string) to go when the dialog is closed. If not specified
 *   it navigates to current location
 * @returns {{setIsLoading: setIsLoading, show, loading, close: close, open: open}}
 */
const useDialog = (path=null, onClosePath=null) => {
    const [state, updateState] = useReducer(
        (oldState, partialState) => ({
            ...oldState,
            ...partialState
        }),
        {
            show: false,
            loading: false
        }
    );

    const navigate = useNavigate();
    const resolvedPath = useResolvedPath(path || "");
    const match = useMatch({path: resolvedPath.pathname, end: false});

    // set open if the given path match
    useEffect(
        () => {
            if(path && match?.pathname){
                updateState({show: true});
            } else{
                updateState({show: false});
            }
        },
        [path, match?.pathname]  // note match always change, match.pathname does not
    );

    const open = () => {
        updateState({show: true});
        if(path){
            navigate(path, {replace: true});
        }
    }

    const close = () => {
        updateState({show: false, loading: false});
        if(path){
            navigate(onClosePath || "", {replace: true});
        }
    }

    const setIsLoading = (state) => {
        updateState({loading: state})
    }

    return {
        show: state.show,
        loading:state.loading,
        open: open,
        close: close,
        setIsLoading,
    }
}

export default useDialog;